import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Cpu, 
  Network,
  Loader2,
  Tag,
  Upload,
  Search,

} from 'lucide-react';
import { adminApi } from '../../utils/admin-api';
import { TagPdfImportDialog } from './TagPdfImportDialog';
import { sessionStorage } from '../../utils/auth-api';
import { API_BASE } from '../../utils/api-base';
import { getWebSocketClient } from '../../utils/websocket-client';
import { toast } from 'sonner';

const parseApiJson = async (response: Response, endpoint: string) => {
  const contentType = (response.headers.get('content-type') || '').toLowerCase();
  const rawText = await response.text();

  if (!rawText) {
    return {} as any;
  }

  if (!contentType.includes('application/json')) {
    const preview = rawText.replace(/\s+/g, ' ').trim().slice(0, 120);
    throw new Error(`Unexpected non-JSON response from ${endpoint} (HTTP ${response.status}). Preview: ${preview}`);
  }

  try {
    return JSON.parse(rawText);
  } catch {
    throw new Error(`Invalid JSON response from ${endpoint} (HTTP ${response.status}).`);
  }
};

const COMMON_TAG_UNITS = [
  { group: 'Electrical', units: ['V', 'mV', 'A', 'mA', 'kW', 'MW', 'kWh', 'MWh', 'kVA', 'kVAR', 'Hz', 'PF', 'Ω'] },
  { group: 'Temperature', units: ['°C', '°F', 'K'] },
  { group: 'Pressure', units: ['bar', 'mbar', 'psi', 'Pa', 'kPa', 'MPa'] },
  { group: 'Flow', units: ['L/min', 'L/h', 'm³/h', 'GPM'] },
  { group: 'Speed', units: ['RPM', 'm/s', 'km/h'] },
  { group: 'Level / Length', units: ['mm', 'cm', 'm', '%'] },
  { group: 'Weight', units: ['g', 'kg', 't'] },
  { group: 'Time', units: ['ms', 's', 'min', 'h'] },
];
const ALL_COMMON_UNITS = COMMON_TAG_UNITS.flatMap(g => g.units);

export function PLCsTab() {
  const [clients, setClients] = useState<any[]>([]);
  const [selectedClient, setSelectedClient] = useState<string>('');
  const [plcs, setPlcs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [tagsDialogOpen, setTagsDialogOpen] = useState(false);
  const [tagSearchQuery, setTagSearchQuery] = useState('');
  const [selectedPLC, setSelectedPLC] = useState<any>(null);
  const [editingTag, setEditingTag] = useState<any>(null);
  const [editingPLC, setEditingPLC] = useState<any>(null);
  const [connectionType, setConnectionType] = useState<'tcp' | 'rtu' | 'siemens-s7'>('tcp');
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<any>(null);
  const [realtimeControlAvailable, setRealtimeControlAvailable] = useState(true);
  const [plcStatusMap, setPlcStatusMap] = useState<Map<string, any>>(new Map());
  const [liveDataMap, setLiveDataMap] = useState<Map<string, any>>(new Map());
  const [pdfImportOpen, setPdfImportOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    protocol: 'modbus',
    connectionType: 'tcp' as 'tcp' | 'rtu' | 'siemens-s7',
    // Modbus TCP
    ipAddress: '',
    port: '502',
    // Modbus RTU
    serialPort: 'COM1',
    baudRate: '9600',
    dataBits: '8',
    stopBits: '1',
    parity: 'none',
    slaveId: '1',
    // Siemens S7
    rack: '0',
    slot: '1',
  });

  const [tagFormData, setTagFormData] = useState({
    name: '',
    address: '',
    dataType: 'float',
    sectionId: '',
    description: '',
    unit: '',
    minValue: 0,
    maxValue: 100,
  });
  const [unitIsCustom, setUnitIsCustom] = useState(false);
  const [sections, setSections] = useState<any[]>([]);
  const [loadingSections, setLoadingSections] = useState(false);
  const [sectionFormData, setSectionFormData] = useState({
    name: '',
    description: '',
  });

  const emptyTagFormData = {
    name: '',
    address: '',
    dataType: 'float',
    sectionId: '',
    description: '',
    unit: '',
    minValue: 0,
    maxValue: 100,
  };

  const session = sessionStorage.get();
  const token = session?.token || '';
  const toPositiveInt = (raw: string, fallback: number) => {
    const parsed = Number.parseInt(String(raw), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
  };
  const toNonNegativeInt = (raw: string, fallback: number) => {
    const parsed = Number.parseInt(String(raw), 10);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
  };

  const getPlcValue = (plc: any, camel: string, snake: string, fallback: any = null) =>
    plc?.[camel] ?? plc?.[snake] ?? fallback;

  const normalizePlcForBridge = (plc: any) => ({
    id: plc.id,
    name: plc.name,
    description: plc.description || '',
    protocol: getPlcValue(plc, 'protocol', 'protocol', 'modbus'),
    connectionType: getPlcValue(plc, 'connectionType', 'connection_type', 'tcp'),
    ipAddress: getPlcValue(plc, 'ipAddress', 'ip_address', ''),
    port: Number(getPlcValue(plc, 'port', 'port', 502)),
    serialPort: getPlcValue(plc, 'serialPort', 'serial_port', ''),
    baudRate: Number(getPlcValue(plc, 'baudRate', 'baud_rate', 9600)),
    dataBits: Number(getPlcValue(plc, 'dataBits', 'data_bits', 8)),
    stopBits: Number(getPlcValue(plc, 'stopBits', 'stop_bits', 1)),
    parity: getPlcValue(plc, 'parity', 'parity', 'none'),
    slaveId: Number(getPlcValue(plc, 'slaveId', 'slave_id', 1)),
    rack: Number(getPlcValue(plc, 'rack', 'rack', 0)),
    slot: Number(getPlcValue(plc, 'slot', 'slot', 1)),
    tags: Array.isArray(plc.tags)
      ? plc.tags.map((tag: any) => ({
          id: tag.id,
          name: tag.name,
          address: tag.address,
          dataType: (() => {
            const rawType = String(tag.dataType || tag.data_type || 'holding').toLowerCase();
            if (rawType === 'float' || rawType === 'float32' || rawType === 'real') return 'float';
            if (rawType === 'int' || rawType === 'integer' || rawType === 'int16' || rawType === 'uint16' || rawType === 'word') return 'int';
            if (rawType === 'bool' || rawType === 'boolean' || rawType === 'coil') return 'bool';
            if (rawType === 'input' || rawType === 'discrete') return rawType;
            return rawType || 'holding';
          })(),
          length: (() => {
            const parsed = Number.parseInt(String(tag.length ?? tag.regCount ?? tag.registerCount ?? ''), 10);
            if (Number.isFinite(parsed) && parsed > 0) return parsed;
            const rawType = String(tag.dataType || tag.data_type || 'holding').toLowerCase();
            if (rawType === 'float' || rawType === 'float32' || rawType === 'real' || rawType === 'holding') return 2;
            return 1;
          })(),
          unit: tag.unit || '',
        }))
      : [],
  });

  useEffect(() => {
    loadClients();
    // Subscribe to PLC status updates
    const wsClient = getWebSocketClient(sessionStorage.get()?.token);
    
    const statusHandler = (data: any) => {
      if (data.type === 'plc_status_update') {
        setPlcStatusMap(prev => new Map(prev).set(data.plcId, {
          connected: !!data.connected,
          reconnecting: !!data.reconnecting,
          reconnectAttempt: data.reconnectAttempt || 0,
          error: data.error,
          timestamp: data.timestamp
        }));
      }
    };

    const connectionResultHandler = (data: any) => {
      if (data?.type !== 'plc_connection_result' || !data?.plcId) return;

      setPlcStatusMap((prev) => new Map(prev).set(data.plcId, {
        connected: !!data.connected,
        reconnecting: false,
        reconnectAttempt: 0,
        error: data.error || null,
        timestamp: new Date().toISOString(),
      }));

      if (data.connected) {
        toast.success(`PLC connected`, { description: data.plcId });
      } else {
        toast.error('PLC connection failed', {
          description: data.error || 'Unknown connection error',
        });
      }
    };

    const liveDataHandler = (data: any) => {
      if (data?.type !== 'plc_live_data' || !data?.plcId) return;

      setLiveDataMap((prev) => {
        const next = new Map(prev);
        const current = next.get(data.plcId) || {};
        const currentTsMs = new Date(current?.timestamp || '').getTime();
        const incomingTsMs = new Date(data.timestamp || '').getTime();
        if (Number.isFinite(currentTsMs) && Number.isFinite(incomingTsMs) && incomingTsMs < currentTsMs) {
          return next;
        }

        const prevValues = current?.values || {};
        const mergedValues: Record<string, any> = { ...prevValues };
        Object.entries(data.values || {}).forEach(([k, v]) => {
          if (v !== null && v !== undefined) {
            mergedValues[k] = v;
          }
        });

        next.set(data.plcId, {
          values: mergedValues,
          timestamp: data.timestamp,
          plcName: data.plcName,
        });
        return next;
      });

      setPlcStatusMap((prev) => {
        const next = new Map(prev);
        const current = next.get(data.plcId) || {};
        next.set(data.plcId, {
          ...current,
          connected: true,
          reconnecting: false,
          error: null,
          timestamp: data.timestamp,
        });
        return next;
      });
    };
    
    wsClient.on('plc_status_update', statusHandler);
    wsClient.on('plc_connection_result', connectionResultHandler);
    wsClient.on('plc_live_data', liveDataHandler);
    
    return () => {
      wsClient.off('plc_status_update', statusHandler);
      wsClient.off('plc_connection_result', connectionResultHandler);
      wsClient.off('plc_live_data', liveDataHandler);
    };
  }, []);

  useEffect(() => {
    if (selectedClient) {
      loadPLCs();
    }
  }, [selectedClient]);

  useEffect(() => {
    if (!token || !selectedClient) return;
    const wsClient = getWebSocketClient(token, { clientId: selectedClient });
    wsClient.canUseRealtimeControl().then((canUseRealtime) => {
      setRealtimeControlAvailable(canUseRealtime);
      // Always connect — in VPS mode this falls back to SSE relay so live data still flows.
      wsClient.connect();
    }).catch(() => {
      setRealtimeControlAvailable(true);
      wsClient.connect();
    });
  }, [token, selectedClient]);

  const loadClients = async () => {
    try {
      const data = await adminApi.getClients(token);
      setClients(data);
      if (data.length > 0) {
        setSelectedClient(data[0].id);
      }
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadPLCs = async () => {
    try {
      setLoading(true);
      const data = await adminApi.getPLCs(token, selectedClient);
      setPlcs(data);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const refreshSelectedPLC = async (plcId: string) => {
    const updatedPLCs = await adminApi.getPLCs(token, selectedClient);
    setPlcs(updatedPLCs);
    const updatedPLC = updatedPLCs.find((plc: any) => plc.id === plcId) || null;
    if (updatedPLC) {
      setSelectedPLC(updatedPLC);
    }
    return updatedPLC;
  };

  const loadSectionsForPLC = async (plcId: string) => {
    if (!plcId || !selectedClient) {
      setSections([]);
      return;
    }

    setLoadingSections(true);
    try {
      const data = await adminApi.getPLCSections(token, selectedClient, plcId);
      setSections(Array.isArray(data) ? data : []);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load sections');
      setSections([]);
    } finally {
      setLoadingSections(false);
    }
  };

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    e?.preventDefault();
    
    const name = formData.name.trim();
    if (!name) {
      toast.error('PLC name is required');
      return;
    }

    if ((connectionType === 'tcp' || connectionType === 'siemens-s7') && !formData.ipAddress.trim()) {
      toast.error(connectionType === 'siemens-s7' ? 'IP address is required for Siemens S7' : 'IP address is required for Modbus TCP');
      return;
    }

    if (connectionType === 'rtu' && !formData.serialPort.trim()) {
      toast.error('Serial port is required for Modbus RTU');
      return;
    }

    const normalizedPayload = {
      ...formData,
      name,
      description: formData.description.trim(),
      connectionType,
      protocol: connectionType === 'siemens-s7' ? 's7' : 'modbus',
      ipAddress: formData.ipAddress.trim(),
      serialPort: formData.serialPort.trim(),
      port: toPositiveInt(formData.port, 502),
      baudRate: toPositiveInt(formData.baudRate, 9600),
      dataBits: toPositiveInt(formData.dataBits, 8),
      stopBits: toPositiveInt(formData.stopBits, 1),
      slaveId: toPositiveInt(formData.slaveId, 1),
      rack: toNonNegativeInt(formData.rack, 0),
      slot: toPositiveInt(formData.slot, 1),
    };

    try {
      if (editingPLC) {
        await adminApi.updatePLC(token, selectedClient, editingPLC.id, normalizedPayload);
        toast.success('PLC updated successfully');
      } else {
        await adminApi.createPLC(token, selectedClient, normalizedPayload);
        toast.success('PLC created successfully');
      }
      
      loadPLCs();
      handleDialogClose();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleEdit = (plc: any) => {
    const normalized = normalizePlcForBridge(plc);
    setEditingPLC(plc);
    setFormData({
      name: normalized.name,
      description: normalized.description || '',
      protocol: normalized.connectionType === 'siemens-s7' ? 's7' : (normalized.protocol || 'modbus'),
      connectionType: normalized.connectionType || 'tcp',
      ipAddress: normalized.ipAddress || '',
      port: String(normalized.port || 502),
      serialPort: normalized.serialPort || 'COM1',
      baudRate: String(normalized.baudRate || 9600),
      dataBits: String(normalized.dataBits || 8),
      stopBits: String(normalized.stopBits || 1),
      parity: normalized.parity || 'none',
      slaveId: String(normalized.slaveId || 1),
      rack: String(Number.isFinite(normalized.rack) ? normalized.rack : 0),
      slot: String(Number.isFinite(normalized.slot) ? normalized.slot : 1),
    });
    setConnectionType(normalized.connectionType || 'tcp');
    setDialogOpen(true);
  };

  const handleDelete = async (plcId: string) => {
    if (!confirm('Are you sure you want to delete this PLC?')) return;
    
    try {
      await adminApi.deletePLC(token, selectedClient, plcId);
      toast.success('PLC deleted successfully');
      loadPLCs();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const connectPLC = async (plc: any) => {
    try {
      const normalized = normalizePlcForBridge(plc);
      console.log('Connecting PLC with data:', normalized);

      if (!Array.isArray(normalized.tags) || normalized.tags.length === 0) {
        toast.error('Add at least one tag before connecting this PLC', {
          description: 'Open Tags and configure register addresses first.',
        });
        return;
      }

      const wsClient = getWebSocketClient(token, { clientId: selectedClient });
      const canUseRealtime = await wsClient.canUseRealtimeControl();
      if (!canUseRealtime) {
        toast.info('Realtime PLC connect is disabled on this host', {
          description: 'Use bridge connectivity checks here, and run live PLC control from the client/gateway host.',
        });
        return;
      }

      wsClient.connect();
      const queuedOrSent = wsClient.connectPLC(normalized);
      if (queuedOrSent) {
        toast.success(`Connecting to ${normalized.name}...`);
      } else {
        toast.error('Bridge websocket is disabled or unavailable on this host');
      }
    } catch (error: any) {
      toast.error(`Failed to connect: ${error.message}`);
    }
  };

  const disconnectPLC = async (plc: any) => {
    try {
      const wsClient = getWebSocketClient(token, { clientId: selectedClient });
      const canUseRealtime = await wsClient.canUseRealtimeControl();
      if (!canUseRealtime) {
        toast.info('Realtime PLC disconnect is disabled on this host', {
          description: 'Run live PLC control from the client/gateway host where bridge websocket is enabled.',
        });
        return;
      }

      wsClient.connect();
      const queuedOrSent = wsClient.disconnectPLC(plc.id);
      if (queuedOrSent) {
        toast.success(`Disconnected from ${plc.name}`);
      } else {
        toast.error('Bridge websocket is disabled or unavailable on this host');
      }
    } catch (error: any) {
      toast.error(`Failed to disconnect: ${error.message}`);
    }
  };

  const handleTestConnection = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedClient) {
      toast.error('Please select a client first');
      return;
    }
    
    if (!formData.name) {
      toast.error('PLC name is required');
      return;
    }

    if ((formData.connectionType === 'tcp' || formData.connectionType === 'siemens-s7') && !formData.ipAddress) {
      toast.error(formData.connectionType === 'siemens-s7' ? 'IP Address is required for Siemens S7 connection' : 'IP Address is required for TCP connection');
      return;
    }

    if (formData.connectionType === 'rtu' && !formData.serialPort) {
      toast.error('Serial Port is required for RTU connection');
      return;
    }

    setTestingConnection(true);
    setConnectionStatus(null);

    try {
      if (!token) {
        throw new Error('Session expired');
      }

      const response = await fetch(`${API_BASE}/api/admin/clients/${selectedClient}/bridge-connectivity`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const connectivity = await parseApiJson(response, '/api/admin/clients/:clientId/bridge-connectivity');
      if (!response.ok) {
        throw new Error(connectivity.error || 'Failed to check bridge connectivity');
      }

      const onlineBridges = Number(connectivity?.counts?.online_bridges || 0);
      const isDataFresh = Boolean(connectivity?.data_flow?.is_data_fresh);
      const hasAnyPlcTags = plcs.some((plc) => Array.isArray(plc?.tags) && plc.tags.length > 0);
      const recommendations = Array.isArray(connectivity?.recommendations)
        ? connectivity.recommendations.join(' ')
        : '';

      const result = onlineBridges > 0 && isDataFresh
        ? {
            success: true,
            message: '✅ Remote bridge test passed',
            details: 'Bridge is online and recent PLC/energy data is reaching the server.',
            timestamp: new Date().toISOString(),
          }
        : onlineBridges > 0
          ? {
              success: false,
              message: hasAnyPlcTags
                ? 'ℹ️ Bridge is online, but data looks stale'
                : 'ℹ️ Bridge is online, but no PLC tags are configured yet',
              details: hasAnyPlcTags
                ? (connectivity?.summary || recommendations || 'No recent data was received from this client.')
                : 'Configure at least one tag under PLC > Tags, then click Connect for that PLC.',
              timestamp: new Date().toISOString(),
            }
          : {
              success: false,
              message: '❌ No online bridge found for this client',
              details: connectivity?.summary || recommendations || 'Ensure bridge service is running on the client machine.',
              timestamp: new Date().toISOString(),
            };

      setConnectionStatus(result);
      
      if (result.success) {
        toast.success(result.message || 'Connection successful!');
      } else {
        toast.info(result.message || 'Remote bridge check completed', {
          description: result.details,
          duration: 10000,
        });
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Connection test failed';
      setConnectionStatus({
        success: false,
        message: `❌ ${errorMsg}`,
        details: 'An error occurred during the connection test'
      });
      toast.error(errorMsg);
    } finally {
      setTestingConnection(false);
    }
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setEditingPLC(null);
    setConnectionType('tcp');
    setConnectionStatus(null);
    setFormData({
      name: '',
      description: '',
      protocol: 'modbus',
      connectionType: 'tcp',
      ipAddress: '',
      port: '502',
      serialPort: 'COM1',
      baudRate: '9600',
      dataBits: '8',
      stopBits: '1',
      parity: 'none',
      slaveId: '1',
      rack: '0',
      slot: '1',
    });
  };

  const openTagsDialog = async (plc: any) => {
    setSelectedPLC(plc);
    setTagsDialogOpen(true);
    setTagSearchQuery('');
    await loadSectionsForPLC(plc.id);
  };

  const handleTagsDialogOpenChange = (open: boolean) => {
    setTagsDialogOpen(open);
    if (!open) {
      setEditingTag(null);
      setTagSearchQuery('');
      setSections([]);
      setSectionFormData({ name: '', description: '' });
      setTagFormData(emptyTagFormData);
      setUnitIsCustom(false);
    }
  };

  const requestBridgeReconnectForPLC = async (plc: any, successMessage = 'Connection request sent to bridge server') => {
    const wsClient = getWebSocketClient(token, { clientId: selectedClient });
    const canUseRealtime = await wsClient.canUseRealtimeControl();
    if (canUseRealtime) {
      wsClient.connect();
      const queuedOrSent = wsClient.connectPLC(normalizePlcForBridge(plc));
      if (queuedOrSent) {
        toast.success(successMessage);
      }
    } else {
      toast.info('Tag saved. Realtime connect command skipped on this host.', {
        description: 'Use Connect from a host where bridge websocket control is enabled.',
      });
    }
  };

  const resetTagForm = () => {
    setEditingTag(null);
    setTagFormData(emptyTagFormData);
    setUnitIsCustom(false);
  };

  const handleEditTag = (tag: any) => {
    const dataType = tag.dataType || tag.data_type || 'float';
    const unit = tag.unit || '';
    setEditingTag(tag);
    setTagFormData({
      name: tag.name || '',
      address: String(tag.address || ''),
      dataType,
      sectionId: tag.sectionId || tag.section_id || '',
      description: tag.description || '',
      unit,
      minValue: Number(tag.minValue ?? tag.min_value ?? 0),
      maxValue: Number(tag.maxValue ?? tag.max_value ?? 100),
    });
    setUnitIsCustom(Boolean(unit) && !ALL_COMMON_UNITS.includes(unit));
  };

  const handleSaveTag = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!tagFormData.name) {
      toast.error('Tag name is required');
      return;
    }

    if (!String(tagFormData.address || '').trim()) {
      toast.error('Tag address is required');
      return;
    }

    if (tagFormData.dataType === 'string') {
      toast.error('String tag type is not supported for live PLC polling. Use Float, Integer, or Boolean.');
      return;
    }

    try {
      const payload = {
        ...tagFormData,
        sectionId: tagFormData.sectionId || null,
      };

      if (editingTag) {
        await adminApi.updateTag(token, selectedClient, selectedPLC.id, editingTag.id, payload);
        toast.success('Tag updated successfully');
      } else {
        await adminApi.addTags(token, selectedClient, selectedPLC.id, [payload]);
        toast.success('Tag added successfully');
      }

      const updatedPLC = await refreshSelectedPLC(selectedPLC.id);
      if (updatedPLC) {
        await requestBridgeReconnectForPLC(updatedPLC);
      }
      resetTagForm();
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleDeleteTag = async (tagId: string) => {
    if (!confirm('Are you sure you want to delete this tag?')) return;
    
    try {
      await adminApi.deleteTag(token, selectedClient, selectedPLC.id, tagId);
      toast.success('Tag deleted successfully');
      if (editingTag?.id === tagId) {
        resetTagForm();
      }
      await refreshSelectedPLC(selectedPLC.id);
    } catch (error: any) {
      toast.error(error.message);
    }
  };

  const handleCreateSection = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedPLC?.id) {
      toast.error('Please open a PLC first');
      return;
    }

    if (!sectionFormData.name.trim()) {
      toast.error('Section name is required');
      return;
    }

    try {
      await adminApi.createPLCSection(token, selectedClient, selectedPLC.id, {
        name: sectionFormData.name.trim(),
        description: sectionFormData.description.trim(),
      });
      toast.success('Section created successfully');
      setSectionFormData({ name: '', description: '' });
      await loadSectionsForPLC(selectedPLC.id);
    } catch (error: any) {
      toast.error(error.message || 'Failed to create section');
    }
  };

  const handleDeleteSection = async (sectionId: string) => {
    if (!selectedPLC?.id) return;
    if (!confirm('Are you sure? Tags in this section will become unassigned.')) return;

    try {
      await adminApi.deletePLCSection(token, selectedClient, selectedPLC.id, sectionId);
      toast.success('Section deleted successfully');
      await loadSectionsForPLC(selectedPLC.id);
      await refreshSelectedPLC(selectedPLC.id);
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete section');
    }
  };

  const handleChangeTagSection = async (tag: any, newSectionId: string) => {
    if (!selectedPLC?.id) return;

    const currentSectionId = String(tag.sectionId || tag.section_id || '');
    if (currentSectionId === newSectionId) {
      return;
    }

    try {
      await adminApi.updateTag(token, selectedClient, selectedPLC.id, tag.id, {
        name: tag.name,
        address: tag.address,
        dataType: tag.dataType || tag.data_type || 'float',
        unit: tag.unit || '',
        description: tag.description || '',
        minValue: Number(tag.minValue ?? tag.min_value ?? 0),
        maxValue: Number(tag.maxValue ?? tag.max_value ?? 100),
        sectionId: newSectionId || null,
      });
      toast.success('Tag section updated');
      await refreshSelectedPLC(selectedPLC.id);
    } catch (error: any) {
      toast.error(error.message || 'Failed to update tag section');
    }
  };

  const sectionNameById = new Map(
    sections.map((section: any) => [String(section.id), section.name || 'Unnamed Section'])
  );

  const tagSearchTerm = tagSearchQuery.trim().toLowerCase();
  const visibleTags = tagSearchTerm
    ? (selectedPLC?.tags || []).filter((tag: any) =>
        String(tag.name || '').toLowerCase().includes(tagSearchTerm) ||
        String(tag.address || '').toLowerCase().includes(tagSearchTerm)
      )
    : (selectedPLC?.tags || []);

  const groupedTags = visibleTags.reduce((acc: any, tag: any) => {
    const sectionId = String(tag.sectionId || tag.section_id || '');
    const key = sectionId || '__unassigned__';
    if (!acc[key]) {
      acc[key] = {
        id: sectionId || null,
        name: sectionId ? (sectionNameById.get(sectionId) || 'Unknown Section') : 'Unassigned',
        tags: [],
      };
    }
    acc[key].tags.push(tag);
    return acc;
  }, {});

  const groupedTagEntries = Object.values(groupedTags).sort((a: any, b: any) => {
    if (a.id === null) return 1;
    if (b.id === null) return -1;
    return String(a.name).localeCompare(String(b.name));
  });

  return (
    <div className="space-y-5 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">PLC Configuration</h2>
          <p className="text-sm sm:text-base text-muted-foreground mt-1">
            Configure PLCs and their tags for each client
          </p>
        </div>
        
        <div className="flex w-full sm:w-auto flex-col sm:flex-row gap-3">
          <Select value={selectedClient} onValueChange={setSelectedClient}>
            <SelectTrigger className="w-full sm:w-[200px]">
              <SelectValue placeholder="Select client" />
            </SelectTrigger>
            <SelectContent>
              {clients.map((client) => (
                <SelectItem key={client.id} value={client.id}>
                  {client.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="gap-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
                disabled={!selectedClient}
              >
                <Plus className="h-4 w-4" />
                Add PLC
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingPLC ? 'Edit PLC' : 'Add New PLC'}</DialogTitle>
                <DialogDescription>
                  Configure PLC connection settings using Modbus TCP, Modbus RTU, or Siemens S7
                </DialogDescription>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto">
                <div className="space-y-2">
                  <Label htmlFor="plc-name">PLC Name *</Label>
                  <Input
                    id="plc-name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Production Line 1"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optional description"
                    rows={2}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Connection Type *</Label>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant={connectionType === 'tcp' ? 'default' : 'outline'}
                      className="flex-1 gap-2"
                      onClick={() => {
                        setConnectionType('tcp');
                        setFormData((prev) => ({ ...prev, connectionType: 'tcp' }));
                      }}
                    >
                      <Network className="h-4 w-4" />
                      Modbus TCP
                    </Button>
                    <Button
                      type="button"
                      variant={connectionType === 'rtu' ? 'default' : 'outline'}
                      className="flex-1 gap-2"
                      onClick={() => {
                        setConnectionType('rtu');
                        setFormData((prev) => ({ ...prev, connectionType: 'rtu' }));
                      }}
                    >
                      <Network className="h-4 w-4" />
                      Modbus RTU
                    </Button>
                    <Button
                      type="button"
                      variant={connectionType === 'siemens-s7' ? 'default' : 'outline'}
                      className="flex-1 gap-2"
                      onClick={() => {
                        setConnectionType('siemens-s7');
                        setFormData((prev) => ({ ...prev, connectionType: 'siemens-s7', protocol: 's7' }));
                      }}
                    >
                      <Cpu className="h-4 w-4" />
                      Siemens S7
                    </Button>
                  </div>
                </div>

                {/* Modbus TCP Settings */}
                {connectionType === 'tcp' && (
                  <div className="space-y-4 p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <h3 className="font-semibold text-blue-900 dark:text-blue-100">Modbus TCP Configuration</h3>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="col-span-2 space-y-2">
                        <Label htmlFor="ip">IP Address *</Label>
                        <Input
                          id="ip"
                          value={formData.ipAddress}
                          onChange={(e) => setFormData({ ...formData, ipAddress: e.target.value })}
                          placeholder="e.g., 192.168.1.100"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="port">Port *</Label>
                        <Input
                          id="port"
                          type="number"
                          value={formData.port}
                          onChange={(e) => setFormData({ ...formData, port: e.target.value })}
                          placeholder="502"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="slave-id">Slave ID</Label>
                        <Input
                          id="slave-id"
                          type="number"
                          value={formData.slaveId}
                          onChange={(e) => setFormData({ ...formData, slaveId: e.target.value })}
                          placeholder="1"
                          min="1"
                          max="247"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Modbus RTU Settings */}
                {connectionType === 'rtu' && (
                  <div className="space-y-4 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                    <h3 className="font-semibold text-amber-900 dark:text-amber-100">Modbus RTU Configuration</h3>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="col-span-2 space-y-2">
                        <Label htmlFor="serial-port">Serial Port *</Label>
                        <Input
                          id="serial-port"
                          value={formData.serialPort}
                          onChange={(e) => setFormData({ ...formData, serialPort: e.target.value })}
                          placeholder="e.g., COM3"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="baud-rate">Baud Rate *</Label>
                        <Select 
                          value={formData.baudRate} 
                          onValueChange={(value) => setFormData({ ...formData, baudRate: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="9600">9600</SelectItem>
                            <SelectItem value="19200">19200</SelectItem>
                            <SelectItem value="38400">38400</SelectItem>
                            <SelectItem value="57600">57600</SelectItem>
                            <SelectItem value="115200">115200</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="parity">Parity *</Label>
                        <Select 
                          value={formData.parity} 
                          onValueChange={(value) => setFormData({ ...formData, parity: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="even">Even</SelectItem>
                            <SelectItem value="odd">Odd</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="data-bits">Data Bits *</Label>
                        <Select 
                          value={formData.dataBits} 
                          onValueChange={(value) => setFormData({ ...formData, dataBits: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="7">7</SelectItem>
                            <SelectItem value="8">8</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="stop-bits">Stop Bits *</Label>
                        <Select 
                          value={formData.stopBits} 
                          onValueChange={(value) => setFormData({ ...formData, stopBits: value })}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1</SelectItem>
                            <SelectItem value="2">2</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="slave-id-rtu">Slave ID *</Label>
                        <Input
                          id="slave-id-rtu"
                          type="number"
                          value={formData.slaveId}
                          onChange={(e) => setFormData({ ...formData, slaveId: e.target.value })}
                          placeholder="1"
                          min="1"
                          max="247"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {connectionType === 'siemens-s7' && (
                  <div className="space-y-4 p-4 bg-violet-50 dark:bg-violet-900/20 rounded-lg border border-violet-200 dark:border-violet-800">
                    <h3 className="font-semibold text-violet-900 dark:text-violet-100">Siemens S7 Configuration</h3>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="col-span-2 space-y-2">
                        <Label htmlFor="s7-ip">IP Address *</Label>
                        <Input
                          id="s7-ip"
                          value={formData.ipAddress}
                          onChange={(e) => setFormData({ ...formData, ipAddress: e.target.value })}
                          placeholder="e.g., 192.168.1.10"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="s7-rack">Rack *</Label>
                        <Input
                          id="s7-rack"
                          type="number"
                          value={formData.rack}
                          onChange={(e) => setFormData({ ...formData, rack: e.target.value })}
                          placeholder="0"
                          min="0"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="s7-slot">Slot *</Label>
                        <Input
                          id="s7-slot"
                          type="number"
                          value={formData.slot}
                          onChange={(e) => setFormData({ ...formData, slot: e.target.value })}
                          placeholder="1"
                          min="1"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {connectionStatus && (
                  <div className={`p-4 rounded-lg border ${
                    connectionStatus.success 
                      ? 'bg-green-50 border-green-200'
                      : (connectionStatus.message?.includes('disabled') || connectionStatus.details?.includes('VPS mode') || connectionStatus.details?.includes('gateway') || connectionStatus.message?.startsWith('ℹ️'))
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-red-50 border-red-200'
                  }`}>
                    <p className={`font-semibold ${
                      connectionStatus.success ? 'text-green-700'
                        : (connectionStatus.message?.includes('disabled') || connectionStatus.details?.includes('VPS mode') || connectionStatus.details?.includes('gateway') || connectionStatus.message?.startsWith('ℹ️'))
                          ? 'text-blue-700'
                          : 'text-red-700'
                    }`}>
                      {connectionStatus.message}
                    </p>
                    {connectionStatus.details && (
                      <p className={`text-sm mt-1 ${
                        connectionStatus.success ? 'text-green-600'
                          : (connectionStatus.message?.includes('disabled') || connectionStatus.details?.includes('VPS mode') || connectionStatus.details?.includes('gateway') || connectionStatus.message?.startsWith('ℹ️'))
                            ? 'text-blue-600'
                            : 'text-red-600'
                      }`}>
                        {connectionStatus.details}
                      </p>
                    )}
                    {connectionStatus.timestamp && (
                      <p className="text-xs text-gray-500 mt-2">
                        {new Date(connectionStatus.timestamp).toLocaleString()}
                      </p>
                    )}
                  </div>
                )}

                  <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
                  <Button 
                    type="button" 
                    variant="outline"
                    onClick={handleTestConnection}
                    disabled={testingConnection || !formData.name}
                    className="flex-1 gap-2"
                  >
                    {testingConnection ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Testing Remote Bridge...
                      </>
                    ) : (
                      <>
                        <Network className="h-4 w-4" />
                        Test Remote Connection
                      </>
                    )}
                  </Button>
                  
                  <Button type="button" onClick={handleSubmit} className="flex-1 gap-2">
                    <Plus className="h-4 w-4" />
                    {editingPLC ? 'Update PLC' : 'Create PLC'}
                  </Button>
                  
                  <Button 
                    type="button" 
                    variant="ghost" 
                    onClick={() => {
                      setDialogOpen(false);
                      handleDialogClose();
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* PLCs List */}
      {!selectedClient ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Network className="h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">Please select a client to view their PLCs</p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : plcs.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Cpu className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No PLCs configured</h3>
            <p className="text-muted-foreground text-sm mb-4">
              Add a PLC to start monitoring
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {plcs.map((plc, index) => (
            <motion.div
              key={plc.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="hover:shadow-lg transition-all">
                <CardHeader>
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-green-500 to-blue-600 flex items-center justify-center relative">
                      <Cpu className="h-6 w-6 text-white" />
                      {/* Connection Status Indicator */}
                      <div className={`absolute bottom-0 right-0 w-4 h-4 rounded-full border-2 border-white ${
                        plcStatusMap.get(plc.id)?.reconnecting
                          ? 'bg-amber-400'
                          : (plcStatusMap.get(plc.id)?.connected ? 'bg-green-500' : 'bg-gray-400')
                      }`} title={plcStatusMap.get(plc.id)?.reconnecting ? 'Reconnecting' : (plcStatusMap.get(plc.id)?.connected ? 'Connected' : 'Disconnected')} />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-lg">{plc.name}</CardTitle>
                        <span
                          className={
                            'text-sm px-2.5 py-1 rounded-full ' + (
                              plcStatusMap.get(plc.id)?.reconnecting
                                ? 'bg-amber-100 text-amber-700'
                                : (plcStatusMap.get(plc.id)?.connected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700')
                            )
                          }
                        >
                          {plcStatusMap.get(plc.id)?.reconnecting ? 'Reconnecting' : (plcStatusMap.get(plc.id)?.connected ? 'Online' : 'Offline')}
                        </span>
                      </div>
                      <CardDescription className="text-sm">
                        {getPlcValue(plc, 'ipAddress', 'ip_address', '') || getPlcValue(plc, 'serialPort', 'serial_port', '') || 'Not configured'} | {String(getPlcValue(plc, 'connectionType', 'connection_type', 'tcp')).toUpperCase()}
                      </CardDescription>
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-3">
                  {plc.description && (
                    <p className="text-sm text-muted-foreground">{plc.description}</p>
                  )}

                  <div className="flex items-center gap-2 text-sm">
                    <Tag className="h-4 w-4 text-muted-foreground" />
                    <span>{plc.tags?.length || 0} tags configured</span>
                  </div>

                  {liveDataMap.get(plc.id) && (
                    <div className="rounded-md border bg-muted/30 p-2">
                      <p className="text-sm text-muted-foreground mb-1">
                        Live values • {new Date(liveDataMap.get(plc.id).timestamp).toLocaleTimeString()}
                      </p>
                      <div className="space-y-1">
                        {Object.entries(liveDataMap.get(plc.id).values || {}).slice(0, 3).map(([tagKey, tagValue]) => (
                          <div key={tagKey} className="flex items-center justify-between text-sm">
                            <span className="truncate mr-2">{tagKey}</span>
                            <span className="font-medium">{tagValue === null ? '—' : String(tagValue)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 pt-2 flex-wrap">
                    {!plcStatusMap.get(plc.id)?.connected ? (
                      <Button
                        size="sm"
                        variant="default"
                        className={`flex-1 ${plcStatusMap.get(plc.id)?.reconnecting ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}`}
                        onClick={() => connectPLC(plc)}
                        disabled={!realtimeControlAvailable || plcStatusMap.get(plc.id)?.reconnecting}
                        title={!realtimeControlAvailable ? 'Realtime control disabled on this host' : undefined}
                      >
                        <Network className="h-3 w-3 mr-1" />
                        {plcStatusMap.get(plc.id)?.reconnecting ? 'Reconnecting...' : 'Connect'}
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="destructive"
                        className="flex-1"
                        onClick={() => disconnectPLC(plc)}
                        disabled={!realtimeControlAvailable}
                        title={!realtimeControlAvailable ? 'Realtime control disabled on this host' : undefined}
                      >
                        <Network className="h-3 w-3 mr-1" />
                        Disconnect
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openTagsDialog(plc)}
                    >
                      <Tag className="h-3 w-3 mr-1" />
                      Tags
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(plc)}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(plc.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}

      {/* Tags Management Dialog */}
      <Dialog open={tagsDialogOpen} onOpenChange={handleTagsDialogOpenChange}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">Manage Tags - {selectedPLC?.name}</DialogTitle>
            <DialogDescription>
              Add sections, assign tags to sections, and manage PLC tag mapping
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Sections */}
            <div className="space-y-4 border-b pb-6">
              <h3 className="text-lg font-semibold">Sections</h3>

              <form onSubmit={handleCreateSection} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="space-y-2 sm:col-span-1">
                  <Label>Section Name *</Label>
                  <Input
                    value={sectionFormData.name}
                    onChange={(e) => setSectionFormData({ ...sectionFormData, name: e.target.value })}
                    placeholder="e.g., Section A"
                  />
                </div>
                <div className="space-y-2 sm:col-span-1">
                  <Label>Description</Label>
                  <Input
                    value={sectionFormData.description}
                    onChange={(e) => setSectionFormData({ ...sectionFormData, description: e.target.value })}
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-2 sm:col-span-1 sm:self-end">
                  <Button type="submit" className="w-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Section
                  </Button>
                </div>
              </form>

              {loadingSections ? (
                <div className="flex items-center gap-2 text-base text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading sections...
                </div>
              ) : sections.length > 0 ? (
                <div className="space-y-2">
                  {sections.map((section: any) => (
                    <div key={section.id} className="flex items-center justify-between border rounded-md p-3">
                      <div>
                        <p className="font-semibold text-base">{section.name}</p>
                        {section.description ? (
                          <p className="text-sm text-muted-foreground">{section.description}</p>
                        ) : null}
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteSection(section.id)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-base text-muted-foreground">No sections yet. Create one to organize tags.</p>
              )}
            </div>

            {/* Add / Edit Tag Form */}
            <form onSubmit={handleSaveTag} className="space-y-4 border-b pb-6">
              <div className="flex items-center justify-between gap-3">
                <h3 className="font-semibold">{editingTag ? 'Edit Tag' : 'Add New Tag'}</h3>
                {editingTag && (
                  <Button type="button" size="sm" variant="outline" onClick={resetTagForm}>
                    Cancel
                  </Button>
                )}
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Tag Name *</Label>
                  <Input
                    value={tagFormData.name}
                    onChange={(e) => setTagFormData({ ...tagFormData, name: e.target.value })}
                    placeholder="e.g., Temperature"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Address</Label>
                  <Input
                    value={tagFormData.address}
                    onChange={(e) => setTagFormData({ ...tagFormData, address: e.target.value })}
                    placeholder="e.g., D100 or Mitsubishi D40000 (D0), D40001 (D1)"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Data Type</Label>
                  <Select 
                    value={tagFormData.dataType}
                    onValueChange={(value) => setTagFormData({ ...tagFormData, dataType: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="float">Float</SelectItem>
                      <SelectItem value="int">Integer</SelectItem>
                      <SelectItem value="bool">Boolean</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Section</Label>
                  <Select
                    value={tagFormData.sectionId || 'none'}
                    onValueChange={(value) => setTagFormData({ ...tagFormData, sectionId: value === 'none' ? '' : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Unassigned</SelectItem>
                      {sections.map((section: any) => (
                        <SelectItem key={section.id} value={section.id}>{section.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Unit</Label>
                  <Select
                    value={unitIsCustom ? '__custom__' : (tagFormData.unit || '__none__')}
                    onValueChange={(val) => {
                      if (val === '__custom__') {
                        setUnitIsCustom(true);
                        setTagFormData({ ...tagFormData, unit: '' });
                      } else if (val === '__none__') {
                        setUnitIsCustom(false);
                        setTagFormData({ ...tagFormData, unit: '' });
                      } else {
                        setUnitIsCustom(false);
                        setTagFormData({ ...tagFormData, unit: val });
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select unit..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— None —</SelectItem>
                      {COMMON_TAG_UNITS.map(group => (
                        <div key={group.group}>
                          <div className="px-2 py-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{group.group}</div>
                          {group.units.map(u => (
                            <SelectItem key={u} value={u}>{u}</SelectItem>
                          ))}
                        </div>
                      ))}
                      <SelectItem value="__custom__">Custom…</SelectItem>
                    </SelectContent>
                  </Select>
                  {unitIsCustom && (
                    <Input
                      value={tagFormData.unit}
                      onChange={(e) => setTagFormData({ ...tagFormData, unit: e.target.value })}
                      placeholder="Enter custom unit"
                      autoFocus
                    />
                  )}
                </div>

                <div className="space-y-2">
                  <Label>Min Value</Label>
                  <Input
                    type="number"
                    value={tagFormData.minValue}
                    onChange={(e) => setTagFormData({ ...tagFormData, minValue: parseFloat(e.target.value) })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Max Value</Label>
                  <Input
                    type="number"
                    value={tagFormData.maxValue}
                    onChange={(e) => setTagFormData({ ...tagFormData, maxValue: parseFloat(e.target.value) })}
                  />
                </div>
              </div>

              <Button type="submit" className="w-full">
                {editingTag ? (
                  <Edit className="h-4 w-4 mr-2" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                {editingTag ? 'Update Tag' : 'Add Tag'}
              </Button>
            </form>

            {/* Existing Tags */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Configured Tags ({selectedPLC?.tags?.length || 0})</h3>
                <Button type="button" variant="outline" size="sm" onClick={() => setPdfImportOpen(true)}>
                  <Upload className="h-4 w-4 mr-1.5" /> Import from PDF
                </Button>
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={tagSearchQuery}
                  onChange={(e) => setTagSearchQuery(e.target.value)}
                  placeholder="Search tags by name or address..."
                  className="pl-9"
                />
              </div>
              {groupedTagEntries.length > 0 ? (
                <div className="space-y-4">
                  {groupedTagEntries.map((group: any) => (
                    <div key={group.id || 'unassigned'} className="space-y-2 rounded-lg border p-3">
                      <div className="flex items-center justify-between">
                        <p className="font-semibold text-base">{group.name}</p>
                        <span className="text-sm text-muted-foreground">{group.tags.length} tags</span>
                      </div>
                      <div className="space-y-2">
                        {group.tags.map((tag: any) => (
                          <div key={tag.id} className="flex flex-col sm:flex-row sm:items-center gap-3 justify-between p-3 border rounded-lg">
                            <div>
                              <p className="font-semibold text-base">{tag.name}</p>
                              <p className="text-sm text-muted-foreground">
                                {tag.address} • {tag.dataType} • {tag.minValue}-{tag.maxValue} {tag.unit}
                              </p>
                            </div>
                            <div className="flex items-center gap-2">
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleEditTag(tag)}
                                title="Edit tag"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Select
                                value={(tag.sectionId || tag.section_id || 'none') as string}
                                onValueChange={(value) => handleChangeTagSection(tag, value === 'none' ? '' : value)}
                              >
                                <SelectTrigger className="w-[180px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">Unassigned</SelectItem>
                                  {sections.map((section: any) => (
                                    <SelectItem key={section.id} value={section.id}>{section.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteTag(tag.id)}
                              >
                                <Trash2 className="h-4 w-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-6">
                  {tagSearchTerm ? 'No tags match your search' : 'No tags configured yet'}
                </p>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {selectedPLC && (
        <TagPdfImportDialog
          open={pdfImportOpen}
          onOpenChange={setPdfImportOpen}
          token={token}
          clientId={selectedClient}
          plcId={selectedPLC.id}
          onImported={() => refreshSelectedPLC(selectedPLC.id)}
        />
      )}
    </div>
  );
}
